import * as React from "react";
import {
  List,
  DataTable,
  ReferenceField,
  ReferenceInput,
  SelectArrayInput,
  AutocompleteInput,
  DateField,
  FunctionField,
  DateInput,
  SearchInput,
  usePermissions,
  Pagination,
  downloadCSV,
  Button,
  useRecordContext,
  Confirm,
  DataProviderContext,
  useNotify,
  useRefresh,
  ArrayField,
  SingleFieldList,
  ChipField,
  useListContext,
} from "react-admin";
import { RESTAURANT_TYPES } from "../enums/RestaurantTypes";
import { PURCHASE_STATUS } from "../enums/PurchaseStatus";
import { startOfDay, subDays } from "date-fns";
import { ROLE_TYPES } from "../enums/Roles";
import PurchaseKanbanContent from "./PurchaseKanbanContent";
import {
  getPurchaseStatusName,
  isDeliveryPurchase,
} from "../utils/PurchaseUtils";
import DeliveryCodeInput from "./DeliveryCodeInput";
import jsonExport from "jsonexport/dist";
import {
  DATE_TIME_FIELD_OPTIONS,
  DATE_TIME_LOCALE,
  formatDateFromISODate,
} from "../utils/DateTimeUtils";
import ShoppingCartCheckout from "@mui/icons-material/ShoppingCartCheckout";
import { getCountries } from "../enums/Countries";
import { PriceField } from "../common/PriceField";

const adminExporter = (purchases) => {
  const purchasesForExport = purchases.map((purchase) => {
    const purchaseForExport = {
      "Fecha Pago": formatDateFromISODate(purchase.paidDate),
      Estado: purchase.status,
      Restaurante: purchase.restaurantData?.name,
      Direccion: purchase.restaurantData?.address,
      Producto: purchase.productData?.name,
      "Precio Original": purchase.productData?.price,
      Descuento: purchase.productData?.discount,
      "Split Morfy": purchase.payment?.morfySplitPercentage,
      "Monto Morfy": purchase.payment?.morfySplitAmount,
      Usuario: purchase.customer,
    };
    return purchaseForExport;
  });
  jsonExport(
    purchasesForExport,
    {
      headers: [
        "Fecha Pago",
        "Estado",
        "Restaurante",
        "Direccion",
        "Producto",
        "Precio Original",
        "Descuento",
        "Split Morfy",
        "Monto Morfy",
        "Usuario",
      ],
    },
    (err, csv) => {
      downloadCSV(csv, "purchases");
    }
  );
};

const PurchaseFilter = () => {
  const { permissions } = usePermissions();
  return [
    ...(permissions === ROLE_TYPES.ADMIN
      ? [
          <SearchInput
            source="deliveryCode"
            placeholder="Buscar por Código"
            alwaysOn
          />,
          <ReferenceInput
            source="customer"
            reference="users"
            filter={{
              active: true,
              role: ROLE_TYPES.CUSTOMER,
            }}
            label="Usuario"
            alwaysOn
          >
            <AutocompleteInput
              filterToQuery={(searchText) => ({ email: searchText })}
              label="Usuario"
              optionText="email"
            />
          </ReferenceInput>,
        ]
      : []),
    <DateInput source="paidDateFrom" label="Fecha Pago Desde" alwaysOn />,
    <DateInput source="paidDateTo" label="Fecha Pago Hasta" />,
    ...(permissions === ROLE_TYPES.ADMIN
      ? [
          <SelectArrayInput
            label="Países"
            source="country"
            choices={getCountries()}
          />,
        ]
      : []),
    ...(permissions === ROLE_TYPES.ADMIN
      ? [
          <SelectArrayInput
            optionText="name"
            label="Estado"
            source="status"
            choices={[
              {
                id: PURCHASE_STATUS.DRAFT,
                name: getPurchaseStatusName(PURCHASE_STATUS.DRAFT),
              },
              {
                id: PURCHASE_STATUS.PENDING_APPROVAL,
                name: getPurchaseStatusName(PURCHASE_STATUS.PENDING_APPROVAL),
              },
              {
                id: PURCHASE_STATUS.ERROR,
                name: getPurchaseStatusName(PURCHASE_STATUS.ERROR),
              },
              {
                id: PURCHASE_STATUS.PAID,
                name: getPurchaseStatusName(PURCHASE_STATUS.PAID),
              },
              {
                id: PURCHASE_STATUS.DELIVERED,
                name: getPurchaseStatusName(PURCHASE_STATUS.DELIVERED),
              },
              {
                id: PURCHASE_STATUS.REFUNDED,
                name: getPurchaseStatusName(PURCHASE_STATUS.REFUNDED),
              },
            ]}
          />,
        ]
      : []),
    ...(permissions === ROLE_TYPES.ADMIN
      ? [
          <ReferenceInput
            label="Restaurante"
            source="restaurant"
            reference="restaurants"
            perPage={100}
            filter={{
              type: RESTAURANT_TYPES.REGULAR,
            }}
            filterToQuery={(searchText) => ({ name: searchText })}
          >
            <AutocompleteInput
              filterToQuery={(searchText) => ({ name: searchText })}
              label="Restaurante"
              optionText="name"
            />
          </ReferenceInput>,
        ]
      : []),
  ];
};

const PurchaseBulkActionButtons = () => {
  const [showBulkDeliverConfirm, setShowBulkDeliverConfirm] =
    React.useState(false);
  const { selectedIds } = useListContext();
  const dataProvider = React.useContext(DataProviderContext);
  const notify = useNotify();
  const refresh = useRefresh();

  const onBulkDeliverClick = () => {
    if (!selectedIds?.length) return;
    setShowBulkDeliverConfirm(true);
  };

  const handleBulkDeliveConfirm = async () => {
    if (!selectedIds?.length) return;

    try {
      const deliveredPurchases = await dataProvider.deliverPurchases(
        "purchases",
        {
          purchaseIds: selectedIds,
        }
      );
      if (deliveredPurchases) {
        notify(`¡${selectedIds?.length} Pedidos entregados con éxito!`, {
          type: "success",
        });
      }
    } catch (error) {
      notify(
        `Ha ocurrido un error al entregar los pedidos: ${error?.body?.message}`,
        { type: "error" }
      );
    } finally {
      setShowBulkDeliverConfirm(false);
      refresh();
    }
  };

  return (
    <>
      <Button label="Entregar" onClick={onBulkDeliverClick}>
        <ShoppingCartCheckout />
      </Button>
      <Confirm
        isOpen={showBulkDeliverConfirm}
        title={`Entregar ${selectedIds?.length} pedidos`}
        content="¿Estás seguro de entregar los pedidos seleccionados?"
        onConfirm={handleBulkDeliveConfirm}
        onClose={() => setShowBulkDeliverConfirm(false)}
      />
    </>
  );
};

const PurchaseList = () => {
  const { permissions } = usePermissions();
  const [showConfirm, setShowConfirm] = React.useState(false);
  const dataProvider = React.useContext(DataProviderContext);
  const [purchaseToDeliver, setPurchaseToDeliver] = React.useState();
  const notify = useNotify();
  const refresh = useRefresh();

  const DeliverButton = () => {
    const record = useRecordContext();

    if (record.status !== PURCHASE_STATUS.PAID) return;
    if (isDeliveryPurchase(record)) return;

    const onDeliverPress = (event) => {
      event.stopPropagation();
      setShowConfirm(true);
      setPurchaseToDeliver(record);
    };

    return <Button label="Entregar" onClick={onDeliverPress} />;
  };

  const handleConfirm = async () => {
    setShowConfirm(false);
    if (!purchaseToDeliver) return;

    try {
      const deliveredPurchase = await dataProvider.deliverPurchase(
        "purchases",
        {
          id: purchaseToDeliver._id,
          deliveryCode: purchaseToDeliver.deliveryCode,
        }
      );
      if (deliveredPurchase) {
        notify("¡Pedido entregado con éxito!", {
          type: "success",
        });
      }
    } catch (error) {
      notify(
        `Ha ocurrido un error al entregar el pedido: ${error?.body?.message}`,
        { type: "error" }
      );
    } finally {
      setPurchaseToDeliver(null);
      refresh();
    }
  };

  const handleDialogClose = () => {
    setPurchaseToDeliver(null);
    setShowConfirm(false);
  };

  return (
    <List
      title="Pedidos"
      filters={PurchaseFilter()}
      filterDefaultValues={{
        paidDateFrom: subDays(startOfDay(new Date()), 15),
      }}
      pagination={
        permissions === ROLE_TYPES.RESTAURANT ? false : <Pagination />
      }
      exporter={permissions === ROLE_TYPES.ADMIN ? adminExporter : false}
    >
      {permissions === ROLE_TYPES.ADMIN ? (
        <>
          <DataTable
            rowClick="show"
            bulkActionButtons={<PurchaseBulkActionButtons />}
          >
            <DataTable.Col source="customer" label="Usuario">
              <ReferenceField
                label="Usuario"
                reference="users"
                source="customer"
                link={false}
              >
                <FunctionField
                  source="firstName"
                  render={(record) =>
                    `${record?.firstName || ""} ${record?.lastName || ""}` ||
                    "-"
                  }
                />
              </ReferenceField>
            </DataTable.Col>
            <DataTable.Col source="productsData" label="Producto(s)">
              <ArrayField label="Producto(s)" source="productsData">
                <SingleFieldList linkType={false}>
                  <ChipField source="name" size="small" />
                </SingleFieldList>
              </ArrayField>
            </DataTable.Col>
            <DataTable.Col source="restaurantData.name" label="Restaurante" />
            <DataTable.Col source="status" label="Estado">
              <FunctionField
                source="status"
                label="Estado"
                render={(record) => getPurchaseStatusName(record.status)}
              />
            </DataTable.Col>
            <DataTable.Col source="paidDate" label="Fecha de Pago">
              <DateField
                source="paidDate"
                label="Fecha de Pago"
                showTime={true}
                locales={DATE_TIME_LOCALE}
                options={DATE_TIME_FIELD_OPTIONS}
                emptyText="-"
              />
            </DataTable.Col>
            <DataTable.Col
              source="deliveredPurchase.deliveredDate"
              label="Fecha de Entrega"
            >
              <DateField
                source="deliveredPurchase.deliveredDate"
                label="Fecha de Entrega"
                showTime={true}
                locales={DATE_TIME_LOCALE}
                options={DATE_TIME_FIELD_OPTIONS}
                emptyText="-"
              />
            </DataTable.Col>
            <DataTable.Col source="totals.paidAmount" label={"Total Pagado"}>
              <PriceField
                source="totals.paidAmount"
                country="country"
                label={"Total Pagado"}
              />
            </DataTable.Col>
            <DataTable.Col
              source="payment.morfySplitAmount"
              label={"Fee Morfy"}
            >
              <PriceField
                source="payment.morfySplitAmount"
                country="country"
                label={"Fee Morfy"}
              />
            </DataTable.Col>
            <DataTable.Col
              source="payment.restaurantSplitAmount"
              label={"Fee Restaurante"}
            >
              <PriceField
                source="payment.restaurantSplitAmount"
                country="country"
                label={"Fee Restaurante"}
              />
            </DataTable.Col>
            <DataTable.Col
              source="totals.serviceCostAmount"
              label={"Costo de servicio"}
            >
              <PriceField
                source="totals.serviceCostAmount"
                country="country"
                label={"Costo de servicio"}
              />
            </DataTable.Col>
            <DataTable.Col>
              <DeliverButton />
            </DataTable.Col>
          </DataTable>
          <Confirm
            isOpen={showConfirm}
            title={`Entregar pedido`}
            content="¿Estás seguro de entregar este pedido?"
            onConfirm={handleConfirm}
            onClose={handleDialogClose}
          />
        </>
      ) : (
        <>
          <DeliveryCodeInput />
          <PurchaseKanbanContent />
        </>
      )}
    </List>
  );
};

export default PurchaseList;
